import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, Award, ExternalLink, X, ZoomIn, ArrowLeft } from "lucide-react";
import { useCertifications } from "@/hooks/usePortfolio";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

export default function CertificationsPage() {
  const { data: certifications, isLoading, error } = useCertifications();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("All");
  const [sortBy, setSortBy] = useState("Most Recent"); // Most Recent, Oldest, Alphabetical
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredAndSorted = useMemo(() => {
    if (!certifications) return [];
    
    let result = [...certifications];
    
    // Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => 
        (c.certificationName && c.certificationName.toLowerCase().includes(q)) ||
        (c.issuingOrganization && c.issuingOrganization.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q))
      );
    }
    
    // Filter by Org
    if (selectedOrg !== "All") {
      result = result.filter(c => c.issuingOrganization === selectedOrg);
    }
    
    // Sort
    if (sortBy === "Most Recent") {
      result.sort((a, b) => new Date(b.issueDate || 0) - new Date(a.issueDate || 0));
    } else if (sortBy === "Oldest") {
      result.sort((a, b) => new Date(a.issueDate || 0) - new Date(b.issueDate || 0));
    } else if (sortBy === "Alphabetical") {
      result.sort((a, b) => (a.certificationName || "").localeCompare(b.certificationName || ""));
    }
    
    return result;
  }, [certifications, searchQuery, selectedOrg, sortBy]);

  const organizations = useMemo(() => {
    if (!certifications) return ["All"];
    const orgs = new Set(certifications.map(c => c.issuingOrganization).filter(Boolean));
    return ["All", ...Array.from(orgs).sort()];
  }, [certifications]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="w-12 h-12 rounded-full border-2 border-[hsl(var(--gold))]/30 border-t-[hsl(var(--gold))] animate-spin" />
      </div>
    );
  }

  if (error || !certifications) return null;

  return (
    <main className="min-h-screen pt-28 pb-20 relative overflow-hidden" data-testid="certifications-page">
      {/* Background glow effects */}
      <div className="fixed top-0 left-1/4 w-[800px] h-[800px] bg-[hsl(var(--gold))]/5 rounded-full blur-[150px] -translate-y-1/2 pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-[hsl(var(--gold-deep))]/5 rounded-full blur-[150px] translate-y-1/3 pointer-events-none" />
      <div className="fixed inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.015] mix-blend-overlay pointer-events-none" />

      <div className="container-x relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 group">
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back to Portfolio
        </Link>
        
        {/* Header */}
        <div className="mb-12 md:mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl font-serif mb-4"
          >
            Professional <span className="italic gold-text">Certifications</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg flex items-center gap-2"
          >
            A verified collection of <span className="text-foreground font-medium">{certifications.length}</span> credentials and achievements.
          </motion.p>
        </div>
        
        {/* Filters and Controls */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass p-4 rounded-2xl mb-12 flex flex-col md:flex-row gap-4 border border-black/5 dark:border-white/5 shadow-sm"
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/50" />
            <input 
              type="text" 
              placeholder="Search certifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent focus:border-[hsl(var(--gold))]/30 rounded-xl py-3 pl-12 pr-4 text-sm outline-none transition-all placeholder:text-muted-foreground/50"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative shrink-0">
              <select 
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="w-full sm:w-[200px] appearance-none bg-black/5 dark:bg-white/5 border border-transparent focus:border-[hsl(var(--gold))]/30 rounded-xl py-3 pl-4 pr-10 text-sm outline-none transition-all cursor-pointer"
              >
                {organizations.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
              <Filter className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
            
            <div className="relative shrink-0">
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-[180px] appearance-none bg-black/5 dark:bg-white/5 border border-transparent focus:border-[hsl(var(--gold))]/30 rounded-xl py-3 pl-4 pr-10 text-sm outline-none transition-all cursor-pointer"
              >
                <option value="Most Recent">Most Recent</option>
                <option value="Oldest">Oldest</option>
                <option value="Alphabetical">Alphabetical</option>
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">▼</div>
            </div>
          </div>
        </motion.div>
        
        {/* Grid Layout */}
        {filteredAndSorted.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSorted.map((cert, index) => {
              const skillsList = cert.skills ? cert.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
              return (
                <motion.div
                  key={cert.id}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.5) }}
                  className="relative rounded-2xl glass grain overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[hsl(var(--gold))]/10 border border-black/5 hover:border-[hsl(var(--gold))]/40 dark:border-white/5 dark:hover:border-[hsl(var(--gold))]/40 group flex flex-col h-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--gold))]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Thumbnail Banner */}
                  <div className="h-40 w-full bg-black/5 dark:bg-white/5 border-b border-black/5 dark:border-white/5 flex items-center justify-center relative overflow-hidden cursor-pointer" onClick={() => cert.certificateImageUrl && setSelectedImage(cert.certificateImageUrl)}>
                    {cert.certificateImageUrl ? (
                      <>
                        <img 
                          src={cert.certificateImageUrl} 
                          alt={cert.certificationName}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                           <div className="px-4 py-2 rounded-full glass border border-white/20 text-white text-xs font-medium flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                             <ZoomIn className="w-3 h-3" /> Preview
                           </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 opacity-50 transition-opacity group-hover:opacity-100">
                         <Award className="w-12 h-12 text-muted-foreground/30 group-hover:text-[hsl(var(--gold))]/40 transition-colors" />
                         <span className="text-[10px] text-muted-foreground uppercase tracking-widest">No Image</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col relative z-10">
                    <h3 className="font-serif text-xl leading-tight mb-1">{cert.certificationName}</h3>
                    <p className="text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] text-sm font-medium tracking-wide mb-4">
                      {cert.issuingOrganization}
                    </p>
                    
                    <div className="flex flex-col gap-2 mb-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground uppercase tracking-wider">Issued</span>
                        <span className="font-medium text-foreground">{cert.issueDate ? dayjs(cert.issueDate).format('MMM YYYY') : 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground uppercase tracking-wider">Expires</span>
                        <span className="font-medium text-foreground">{cert.doesNotExpire ? 'No Expiration' : (cert.expirationDate ? dayjs(cert.expirationDate).format('MMM YYYY') : 'N/A')}</span>
                      </div>
                      {cert.credentialId && (
                        <div className="flex items-center justify-between text-xs pt-2 border-t border-black/5 dark:border-white/5 mt-1">
                          <span className="text-muted-foreground uppercase tracking-wider">Credential ID</span>
                          <span className="font-mono text-foreground/80">{cert.credentialId}</span>
                        </div>
                      )}
                    </div>
                    
                    {cert.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3 leading-relaxed">
                        {cert.description}
                      </p>
                    )}
                    
                    <div className="mt-auto">
                      {skillsList.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-6">
                          {skillsList.map((skill, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {cert.credentialUrl && (
                        <div className="pt-4 border-t border-black/5 dark:border-white/5">
                          <button 
                            onClick={() => window.open(cert.credentialUrl, "_blank", "noopener,noreferrer")}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[hsl(var(--gold))]/10 hover:bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] text-sm font-medium transition-colors"
                          >
                            View Credential <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-20 text-center flex flex-col items-center justify-center"
          >
            <div className="w-16 h-16 rounded-full glass flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-muted-foreground/50" />
            </div>
            <h3 className="text-xl font-serif mb-2">No certifications found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters to find what you're looking for.</p>
            <button 
              onClick={() => { setSearchQuery(""); setSelectedOrg("All"); }}
              className="mt-6 text-[hsl(var(--gold))] hover:underline text-sm font-medium"
            >
              Clear filters
            </button>
          </motion.div>
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
          >
            <div 
              className="absolute inset-0 bg-background/80 backdrop-blur-md"
              onClick={() => setSelectedImage(null)}
            />
            
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-4xl w-full max-h-[90vh] glass rounded-2xl overflow-hidden border border-white/10 shadow-2xl flex flex-col"
            >
              <button 
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white backdrop-blur-md flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="w-full h-full overflow-auto bg-black/5 dark:bg-black/40 flex items-center justify-center p-4">
                <img 
                  src={selectedImage} 
                  alt="Certificate Preview"
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
